import React, { useState } from "react";
import { reportsService } from "@/legacy/base44Client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  MapPin,
  Calendar,
  TrendingUp,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Clock,
  Filter,
  Loader2
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

const statusColors = {
  reported: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300", border: "border-red-300", icon: AlertCircle },
  acknowledged: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-300", icon: Clock },
  in_progress: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", border: "border-blue-300", icon: TrendingUp },
  waiting_contractor: { bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-300", border: "border-orange-300", icon: Clock },
  dependent_on: { bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-300", border: "border-purple-300", icon: Clock },
  resolved: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300", border: "border-green-300", icon: CheckCircle2 },
  closed: { bg: "bg-gray-100 dark:bg-gray-700", text: "text-gray-700 dark:text-gray-300", border: "border-gray-300", icon: CheckCircle2 },
};

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  other: "📍"
};

export default function MyReports() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { user } = useAuth();

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['myReports', user?.id],
    queryFn: async () => {
      if (user?.user_role === 'city_worker') {
        return await reportsService.list('-created_date');
      }
      return await reportsService.filter({ created_by: user?.id }, '-created_date');
    },
    enabled: !!user,
    initialData: [],
  });

  const filteredReports = reports.filter(report => {
    const statusMatch = selectedStatus === "all" || report.status === selectedStatus;
    const categoryMatch = selectedCategory === "all" || report.category === selectedCategory;
    const searchMatch = !searchTerm || 
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase());
    return statusMatch && categoryMatch && searchMatch;
  });

  const stats = {
    total: reports.length,
    reported: reports.filter(r => r.status === 'reported').length,
    in_progress: reports.filter(r => r.status === 'in_progress').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-4 dark:text-white">Please Login</h2>
          <Button onClick={() => navigate('/Login')}>
            Login to View Reports
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-8 overflow-y-auto">
      <div className="max-w-7xl mx-auto pb-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {user.user_role === 'city_worker' ? 'All Reports' : 'My Reports'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {user.user_role === 'city_worker' 
                ? 'Manage all community reports' 
                : 'Track your submitted issues'}
            </p>
          </div>
          {user.user_role !== 'city_worker' && (
            <Button
              onClick={() => navigate(createPageUrl("ReportIssue"))}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Report
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 md:p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total</p>
            <p className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
          </Card>
          <Card className="p-4 md:p-5 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-red-200 dark:border-red-800">
            <p className="text-sm text-red-600 dark:text-red-400 mb-1">Reported</p>
            <p className="text-2xl md:text-3xl font-bold text-red-700 dark:text-red-300">{stats.reported}</p>
          </Card>
          <Card className="p-4 md:p-5 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">In Progress</p>
            <p className="text-2xl md:text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.in_progress}</p>
          </Card>
          <Card className="p-4 md:p-5 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 border-green-200 dark:border-green-800">
            <p className="text-sm text-green-600 dark:text-green-400 mb-1">Resolved</p>
            <p className="text-2xl md:text-3xl font-bold text-green-700 dark:text-green-300">{stats.resolved}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="pothole">Pothole</SelectItem>
                <SelectItem value="blocked_drain">Blocked Drain</SelectItem>
                <SelectItem value="waste_collection">Waste</SelectItem>
                <SelectItem value="street_lighting">Lighting</SelectItem>
                <SelectItem value="flooding">Flooding</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Reports List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="p-6 animate-pulse bg-white dark:bg-gray-800">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
              </Card>
            ))}
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="p-12 text-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-md">
            <AlertCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Reports Found</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {searchTerm || selectedStatus !== 'all' || selectedCategory !== 'all'
                ? 'Try adjusting your filters'
                : 'Start by reporting an issue in your community'}
            </p>
            {user.user_role !== 'city_worker' && (
              <Button
                onClick={() => navigate(createPageUrl("ReportIssue"))}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Report Your First Issue
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map((report) => {
              const statusStyle = statusColors[report.status] || statusColors.reported;
              const StatusIcon = statusStyle.icon || AlertCircle;
              return (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -4, scale: 1.01 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card 
                    className="p-4 md:p-5 lg:p-6 cursor-pointer hover:shadow-xl transition-all bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-emerald-100 dark:border-emerald-900 overflow-hidden"
                    onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}&from=MyReports`))}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-2xl sm:text-3xl flex-shrink-0">{categoryIcons[report.category]}</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-2 line-clamp-2 break-words">
                          {report.title}
                        </h3>
                        <Badge className={`${statusStyle.bg} ${statusStyle.text} text-xs whitespace-nowrap`}>
                          <StatusIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{report.status.replace('_', ' ')}</span>
                        </Badge>
                      </div>
                    </div>

                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 break-words">
                      {report.description}
                    </p>

                    <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{report.neighborhood || report.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{format(new Date(report.created_date), 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-3 h-3 flex-shrink-0" />
                          <span>{report.upvote_count || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3 flex-shrink-0" />
                          <span>{report.comment_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}